import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { CURRENCY_INFO, DISPLAY_CURRENCIES, type DisplayCurrency } from '@/lib/currency';
import { useCurrencyStore } from '@/stores/currencyStore';

type CurrencySelectorProps = {
  className?: string;
};

export function CurrencySelector({ className }: CurrencySelectorProps) {
  const displayCurrency = useCurrencyStore((s) => s.currency);
  const setDisplayCurrency = useCurrencyStore((s) => s.setCurrency);
  const { flag, name } = CURRENCY_INFO[displayCurrency];
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  return (
    <div ref={rootRef} className={clsx('relative', open && 'z-[100]', className)}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${displayCurrency} display currency, ${name}`}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-lg px-2 py-2 text-white/80 transition hover:bg-white/10 hover:text-white"
      >
        <span className="text-base leading-none" aria-hidden>
          {flag}
        </span>
        <span className="text-xs font-semibold">{displayCurrency}</span>
      </button>

      {open ? (
        <ul
          role="listbox"
          aria-label="Choose display currency"
          className="absolute right-0 top-full z-[100] mt-1 max-h-64 min-w-[9.5rem] overflow-y-auto rounded-lg border border-white/15 bg-brand-900 py-1 shadow-lg"
        >
          {DISPLAY_CURRENCIES.map((c) => {
            const info = CURRENCY_INFO[c];
            const selected = c === displayCurrency;
            return (
              <li key={c} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    setDisplayCurrency(c);
                    setOpen(false);
                  }}
                  className={clsx(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-white/90 transition hover:bg-white/10 hover:text-white',
                    selected && 'bg-white/10 text-white',
                  )}
                >
                  <span className="text-sm leading-none" aria-hidden>
                    {info.flag}
                  </span>
                  <span>{c}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
